/* eslint max-lines: ["error", {max: 395}] -- this overrides for the entire file */
import { compareAsc, format } from "date-fns";
import isEmpty from "lodash/isEmpty";

import { DateFormat } from "@/constants/DateFormat";
import { RecurrenceValue } from "@/constants/Schedule";
import type { ScheduleEventCardProps } from "@/ui/components/schedule/ScheduleEventCard";
import type { AvatarCardProps } from "@/ui/core/AvatarCard";
import { formatISODate } from "@/utils/date.util";
import { getPreferredFullName } from "@/utils/user.util";

const doesRecur = (
  frequency?: AppointmentRecurrenceResponse | EventRecurrence,
): boolean => {
  if (isEmpty(frequency)) {
    return false;
  }

  // Since frequency is typed as a union of string literals,
  // we can directly compare it to the known non-repeating values.
  // Safer and more explicit than comparing with a string and handling casing.
  if (frequency === "Does not repeat" || frequency === "does not repeat") {
    return false;
  }

  return true;
};

const appointmentRecurrenceToFrequency = (
  frequency?: AppointmentRecurrenceResponse,
): EventRecurrence => {
  if (frequency === "biweekly" || frequency === "Biweekly") {
    return RecurrenceValue.Biweekly;
  }
  if (frequency === "weekly" || frequency === "Weekly") {
    return RecurrenceValue.Weekly;
  }

  return RecurrenceValue.DoesNotRepeat;
};

const getGroupClassHosts = (
  event: GroupClassEventResponse,
): AvatarCardProps[] => {
  return event.hosts.map((host) => ({
    pronouns: host.pronouns,
    subtitle: host.providerType,
    userId: host.externalId,
    userName: getPreferredFullName({
      firstName: host.firstName,
      lastName: host.lastName,
    }),
    userType: "provider",
  }));
};

// Adapts a v2 group section event to the v1 GroupClassEventResponse shape so the
// existing combine/card/decline logic works unchanged behind the redesign flag.
// v2 has no description / isSensitive, so those are defaulted; iconName and core drive the
// redesigned schedule card and render once the backend includes them on the event.
const groupSectionEventToGroupClassEvent = (
  event: GroupSectionEventResponse,
): GroupClassEventResponse => ({
  description: "",
  endTime: event.endTime,
  frequency: event.frequency,
  groupClassEventExternalId: event.id,
  groupClassExternalId: event.groupId,
  groupSectionId: event.groupSectionId,
  hosts: event.hosts.map((host) => ({
    externalId: host.externalId,
    firstName: host.firstName,
    lastName: host.lastName,
    pronouns: host.pronouns,
    providerType: host.providerType ?? "",
    type: host.type,
  })),
  iconName: event.iconName ?? "",
  isCore: event.isCore ?? false,
  isSensitive: false,
  startTime: event.startTime,
  title: event.title,
});

const getAppointmentAttendees = (event: AppointmentDetailResponse) => {
  const supportAttendees: AvatarCardProps[] =
    event.supportAttendees?.map((attendee) => ({
      pronouns: attendee.pronouns,
      userId: attendee.externalId,
      userName: getPreferredFullName({
        firstName: attendee.firstName,
        lastName: attendee.lastName,
      }),
      userType: "support",
    })) || [];

  const providerAttendees: AvatarCardProps[] =
    event.providerAttendees?.map((attendee) => ({
      pronouns: attendee.pronouns,
      subtitle: attendee.providerType,
      userId: attendee.externalId,
      userName: attendee.name,
      userType: "provider",
    })) || [];

  const attendees: AvatarCardProps[] = [
    ...providerAttendees,
    ...supportAttendees,
    ...(event.isPatientAttending
      ? [
          {
            pronouns: event.patient.pronouns,
            userId: event.patient.externalId,
            userName: getPreferredFullName({
              chosenName: event.patient.chosenName,
              firstName: event.patient.firstName,
              lastName: event.patient.lastName,
            }),
            userType: "patient" as const,
          },
        ]
      : []),
    {
      pronouns: event.host.pronouns,
      userId: event.host.externalId,
      userName: event.host.name,
      userType: "provider",
    },
  ];
  return attendees;
};

type ScheduleEventProps = Omit<
  ScheduleEventCardProps,
  "onJoinZoom" | "onSelect" | "testID"
>;

// ScheduleEventProps rightfully requires selectedUserId to be non-null/non-undefined
// however, from the GroupClassEventResponse object we do not have the selectedUserId,
// so this typing is more accurate to help reduce potential of bugs (or need to mask undefined as "")
type ScheduleEventPropsPatientOptional = { selectedUserId?: string } & Omit<
  ScheduleEventProps,
  "selectedUserId"
>;

const getGroupClassEventProps = (
  event: GroupClassEventResponse,
): ScheduleEventPropsPatientOptional => {
  return {
    attendees: getGroupClassHosts(event),
    description: event.description,
    endDate: event.endTime,
    externalId: event.groupClassEventExternalId,
    frequency: event.frequency,
    groupClassExternalId: event.groupClassExternalId,
    groupSectionId: event.groupSectionId,
    hasCoreTag: event.isCore,
    iconName: event.iconName,
    isGroupClassEvent: true,
    isRecurring: doesRecur(event.frequency),
    isSensitive: event.isSensitive,
    selectedUserId: undefined,
    startDate: event.startTime,
    title: event.title,
  };
};

const getAppointmentEventProps = (
  event: AppointmentDetailResponse,
): ScheduleEventProps => {
  return {
    attendees: getAppointmentAttendees(event),
    description: event.description,
    endDate: event.appointmentEndDateTime,
    externalId: event.appointmentExternalId,
    frequency: appointmentRecurrenceToFrequency(event.recurrence),
    isPhoneCallEvent: !event.hasZoomCall,
    isRecurring: doesRecur(event.recurrence),
    selectedUserId: event.patient.externalId,
    startDate: event.appointmentStartDateTime,
    title: event.appointmentType,
    typeGroup: event.typeGroup,
  };
};

const getScheduleEventProps = ({
  event,
  isGroupClassEvent,
}: {
  event: AppointmentDetailResponse | GroupClassEventResponse;
  isGroupClassEvent: boolean;
}): ScheduleEventPropsPatientOptional => {
  if (isGroupClassEvent) {
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- legitimate use case
    return getGroupClassEventProps(event as GroupClassEventResponse);
  }
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- legitimate use case
  return getAppointmentEventProps(event as AppointmentDetailResponse);
};

const isAppointmentDetailResponse = (
  event: ScheduledEventResponseItem,
): event is { isGroupClassEvent: false } & AppointmentDetailResponse => {
  return event.isGroupClassEvent === false;
};

const getAppointmentDetailResponseFromScheduleEvent = (
  event: ScheduledEventResponseItem,
): AppointmentDetailResponse | undefined => {
  if (isAppointmentDetailResponse(event)) {
    return event;
  }

  return undefined;
};

const getRecurrenceString = ({
  frequency,
  startDate,
}: {
  frequency: EventRecurrence;
  startDate: string;
}): string => {
  const start = new Date(startDate);
  const dayOfWeek = format(start, DateFormat.weekDay);

  const recurrenceMap: Record<RecurrenceValue, string> = {
    [RecurrenceValue.Biweekly]: `Every other ${dayOfWeek}`,
    [RecurrenceValue.DoesNotRepeat]: "Does not repeat",
    [RecurrenceValue.Monthly]: `Every 4th ${dayOfWeek}`,
    [RecurrenceValue.Weekly]: `Every ${dayOfWeek}`,
  };

  return recurrenceMap[frequency] || RecurrenceValue.DoesNotRepeat;
};

const combineAppointmentsWithClassEvents = ({
  appointmentEvents,
  classEvents,
}: {
  appointmentEvents?: AppointmentDetailResponse[];
  classEvents?: GroupClassEventResponse[];
}): ScheduledEventResponseItem[] => {
  const allAppointments = appointmentEvents ?? [];
  const allClassEvents = classEvents ?? [];

  const scheduledEvents: ScheduledEventResponseItem[] = [
    ...allAppointments.map((appointment) => ({
      ...appointment,
      isGroupClassEvent: false,
    })),
    ...allClassEvents.map((event) => ({
      ...event,
      isGroupClassEvent: true,
    })),
  ];

  return scheduledEvents;
};

const groupCombinedEventsByDate = ({
  combinedEvents,
}: {
  combinedEvents: CombinedScheduleEventsResponse;
}): GroupedCombinedScheduleDataListType[] => {
  if (isEmpty(combinedEvents)) {
    return [];
  }

  const { appointmentEvents, classEvents } = combinedEvents;

  const combined = combineAppointmentsWithClassEvents({
    appointmentEvents: appointmentEvents?.data || [],
    classEvents: classEvents?.data.data || [],
  });

  // eslint-disable-next-line max-params
  combined.sort((a, b) => {
    const dateA = new Date(getEventStartDate(a)).getTime();
    const dateB = new Date(getEventStartDate(b)).getTime();
    return dateA - dateB;
  });

  const grouped: { [key: string]: ScheduledEventResponseItem[] } = {};

  combined.forEach((item) => {
    const eventDate = scheduledDateFormat({ event: item });

    if (!(eventDate in grouped)) {
      grouped[eventDate] = [];
    }

    grouped[eventDate].push(item);
  });

  const groupedArray: GroupedCombinedScheduleDataListType[] = Object.keys(
    grouped,
  )
    // eslint-disable-next-line max-params
    .sort((a, b) => {
      // groupedArray is of the form [{"Thursday, July 2": [...apptObjects], "Friday, July 3": [...aptObjects] }
      // (where ...apptObjects is sorted from earliest timestamp to latest, above via `combined.sort(...)`)
      const earliestEventA = grouped[a][0];
      const earliestEventB = grouped[b][0];
      const dateA = new Date(getEventStartDate(earliestEventA));
      const dateB = new Date(getEventStartDate(earliestEventB));
      return compareAsc(dateA, dateB);
    })
    .map((date) => ({
      data: grouped[date],
      title: date,
    }));

  return groupedArray;
};

const getEventStartDate = (
  event: AppointmentDetailResponse | GroupClassEventResponse,
): string => {
  return "appointmentStartDateTime" in event
    ? event.appointmentStartDateTime
    : event.startTime;
};

const scheduledDateFormat = ({
  event,
}: {
  event: AppointmentDetailResponse | GroupClassEventResponse;
}): string => {
  const startDate = getEventStartDate(event);
  return formatISODate({
    dateString: startDate,
    format: DateFormat.weekDayMonthDay,
  });
};

const transformZoomUrlResponse = ({
  isGroupClass,
  isGroupClassRedesignEnabled,
  response,
}: {
  isGroupClass: boolean;
  isGroupClassRedesignEnabled?: boolean;
  response:
    | AppointmentZoomUrlApiResponse
    | GroupClassZoomUrlApiResponse
    | GroupSectionEventZoomUrlApiResponse;
}): ZoomUrlResponse => {
  if (!isGroupClass) {
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- legitimate use case
    const result = response as AppointmentZoomUrlApiResponse;
    const { id, joinUrl } = result;
    return {
      id,
      joinUrl,
    };
  }

  // v2 group-section-event start returns the payload directly (no `data` wrapper). App
  // users are always joinees (never hosts), so we only need joinUrl.
  if (isGroupClassRedesignEnabled) {
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- legitimate use case
    const result = response as GroupSectionEventZoomUrlApiResponse;
    return {
      id: result.id,
      joinUrl: result.joinUrl,
    };
  }

  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- legitimate use case
  const result = response as GroupClassZoomUrlApiResponse;
  const { id } = result.data;
  const { joinUrl } = result.data;
  return {
    id,
    joinUrl,
  };
};

export {
  appointmentRecurrenceToFrequency,
  doesRecur,
  getAppointmentAttendees,
  getAppointmentDetailResponseFromScheduleEvent,
  getAppointmentEventProps,
  getGroupClassEventProps,
  getGroupClassHosts,
  getRecurrenceString,
  getScheduleEventProps,
  groupCombinedEventsByDate,
  groupSectionEventToGroupClassEvent,
  transformZoomUrlResponse,
};
